// frontend/src/utils/clientSimilarityHelper.ts
// Helper functions for checking client name similarity with Levenshtein distance
// UPDATED: Added excludeClientId parameter for edit mode

import { Client } from '../services/api';

/**
 * Normalizes a client name for comparison
 * - Converts to lowercase
 * - Removes extra whitespace
 * - Removes special characters
 */
const normalizeClientName = (name: string): string => {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s]/g, '') // Remove special characters
    .replace(/\s+/g, ' ') // Replace multiple spaces with single space
    .trim();
};

/**
 * Calculate Levenshtein distance between two strings
 * Returns the minimum number of edits (insertions, deletions, substitutions) needed
 */
const levenshteinDistance = (str1: string, str2: string): number => {
  const len1 = str1.length;
  const len2 = str2.length;
  const matrix: number[][] = [];

  // Initialize matrix
  for (let i = 0; i <= len1; i++) {
    matrix[i] = [i];
  }
  for (let j = 0; j <= len2; j++) {
    matrix[0][j] = j;
  }

  // Fill matrix
  for (let i = 1; i <= len1; i++) {
    for (let j = 1; j <= len2; j++) {
      if (str1[i - 1] === str2[j - 1]) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1, // substitution
          matrix[i][j - 1] + 1, // insertion
          matrix[i - 1][j] + 1 // deletion
        );
      }
    }
  }

  return matrix[len1][len2];
};

/**
 * Calculate similarity percentage between two strings
 * Returns a value between 0 (completely different) and 1 (identical)
 */
const calculateSimilarity = (str1: string, str2: string): number => {
  const distance = levenshteinDistance(str1, str2);
  const maxLength = Math.max(str1.length, str2.length);

  if (maxLength === 0) return 1;

  return 1 - distance / maxLength;
};

/**
 * Enhanced string similarity check for client names using AVERAGE similarity
 * Returns true if the average of first and last name similarities exceeds threshold
 */
const areNamesSimilar = (
  firstName1: string,
  lastName1: string,
  firstName2: string,
  lastName2: string
): boolean => {
  const normalizedFirst1 = normalizeClientName(firstName1);
  const normalizedLast1 = normalizeClientName(lastName1);
  const normalizedFirst2 = normalizeClientName(firstName2);
  const normalizedLast2 = normalizeClientName(lastName2);

  // Skip if any name is too short after normalization
  if (
    normalizedFirst1.length < 2 ||
    normalizedLast1.length < 2 ||
    normalizedFirst2.length < 2 ||
    normalizedLast2.length < 2
  ) {
    return false;
  }

  // Check exact match after normalization
  if (
    normalizedFirst1 === normalizedFirst2 &&
    normalizedLast1 === normalizedLast2
  ) {
    return true;
  }

  // Calculate similarity for first and last names
  const firstNameSimilarity = calculateSimilarity(
    normalizedFirst1,
    normalizedFirst2
  );
  const lastNameSimilarity = calculateSimilarity(
    normalizedLast1,
    normalizedLast2
  );

  // NEW APPROACH: Use average similarity instead of requiring both to exceed threshold
  const averageSimilarity = (firstNameSimilarity + lastNameSimilarity) / 2;

  // Trigger warning if average similarity is >= 90%
  const SIMILARITY_THRESHOLD = 0.8;
  // SETTINGS:
  // 90% (current) = Balanced - catches "Michael Dekker" vs "Michael Doctor" (avg: 75%)
  // 80% = More strict (fewer warnings)
  // 70% = Less strict (more warnings, catches more variations)
  // 65% = Very permissive (may have false positives)

  if (averageSimilarity >= SIMILARITY_THRESHOLD) {
    return true;
  }

  // Also check for swapped names (first/last name reversed)
  const swappedFirstSimilarity = calculateSimilarity(
    normalizedFirst1,
    normalizedLast2
  );
  const swappedLastSimilarity = calculateSimilarity(
    normalizedLast1,
    normalizedFirst2
  );

  const swappedAverageSimilarity =
    (swappedFirstSimilarity + swappedLastSimilarity) / 2;

  if (swappedAverageSimilarity >= SIMILARITY_THRESHOLD) {
    return true;
  }

  return false;
};

/**
 * Finds similar clients from the existing client list
 * Returns array of clients with similar names
 * @param newFirstName - The first name to check
 * @param newLastName - The last name to check
 * @param existingClients - The list of existing clients
 * @param excludeClientId - Optional: Client ID to exclude (for edit mode)
 */
export const findSimilarClients = (
  newFirstName: string,
  newLastName: string,
  existingClients: Client[],
  excludeClientId?: number
): Client[] => {
  if (
    !newFirstName ||
    !newLastName ||
    newFirstName.trim().length < 2 ||
    newLastName.trim().length < 2
  ) {
    return [];
  }

  return existingClients.filter((client) => {
    // Exclude the client being edited
    if (excludeClientId && client.id === excludeClientId) {
      return false;
    }
    return areNamesSimilar(
      newFirstName,
      newLastName,
      client.firstName,
      client.lastName
    );
  });
};

/**
 * Checks if a client name is similar to any existing client
 * Returns the first similar client found, or null
 * @param newFirstName - The first name to check
 * @param newLastName - The last name to check
 * @param existingClients - The list of existing clients
 * @param excludeClientId - Optional: Client ID to exclude (for edit mode)
 */
export const findFirstSimilarClient = (
  newFirstName: string,
  newLastName: string,
  existingClients: Client[],
  excludeClientId?: number
): Client | null => {
  const similar = findSimilarClients(
    newFirstName,
    newLastName,
    existingClients,
    excludeClientId
  );
  return similar.length > 0 ? similar[0] : null;
};
